import { useEffect, useState } from "react";
import { Copy, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";
import { PRODUCTS } from "@/lib/products";
import { Input } from "@/components/ui/input";

export const AdminShopify = ({ adminKey }) => {
  const headers = { "X-Admin-Key": adminKey };
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const site = window.location.origin;
  const [siteUrl, setSiteUrl] = useState(site);
  const [snippet, setSnippet] = useState("");
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    http.post("/admin/shopify/snippet", { site_url: siteUrl }, { headers }).then((r) => setSnippet(r.data.snippet)).catch(() => setSnippet(""));
  }, [siteUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const install = async () => {
    setInstalling(true);
    try {
      const { data } = await http.post("/admin/shopify/install-theme", { site_url: siteUrl }, { headers });
      toast.success(`Configurateur installé sur le thème « ${data.theme} »`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setInstalling(false);
    }
  };

  useEffect(() => {
    http.get("/admin/shopify/status", { headers }).then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sync = async () => {
    setBusy(true);
    try {
      const products = PRODUCTS.map((p) => ({ handle: p.id, title: p.name, description: p.description, category: p.category, image_url: site + p.images[0] }));
      const { data } = await http.post("/admin/shopify/sync", { products }, { headers });
      setResults(data.results);
      toast.success("Synchronisation terminée");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const copy = () => navigator.clipboard.writeText(snippet).then(() => toast.success("Code copié"));

  return (
    <div className="space-y-8" data-testid="admin-shopify">
      <section className="bg-white rounded-2xl border border-[#DDD3C6] p-6 space-y-4">
        <h3 className="font-display text-2xl">1 · Produits « Nos modèles » sur Shopify</h3>
        <p className="text-sm text-[#6B5F53]" data-testid="shopify-status">
          Boutique : <strong>{status?.shop || "—"}</strong> · {status?.configured ? "connectée" : "non connectée (Client ID / secret manquants)"}
        </p>
        <button onClick={sync} disabled={!status?.configured || busy} className="btn-gold disabled:opacity-50" data-testid="shopify-sync-button">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Créer / synchroniser les {PRODUCTS.length} produits
        </button>
        {results.length > 0 && (
          <ul className="text-sm space-y-1" data-testid="shopify-sync-results">
            {results.map((r) => <li key={r.handle}><strong>{r.handle}</strong> : {r.status}{r.error ? ` — ${r.error}` : ""}</li>)}
          </ul>
        )}
      </section>
      <section className="bg-white rounded-2xl border border-[#DDD3C6] p-6 space-y-4">
        <h3 className="font-display text-2xl">2 · Installer le configurateur sur les fiches produit</h3>
        <label className="block space-y-1"><span className="label-xs">Adresse du site publié</span>
          <Input value={siteUrl} onChange={(e) => setSiteUrl(e.target.value.trim())} className="h-11" data-testid="shopify-site-url-input" /></label>
        <button onClick={install} disabled={!status?.configured || installing} className="btn-gold disabled:opacity-50" data-testid="shopify-install-theme-button">
          {installing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Installer automatiquement sur le thème publié
        </button>
      </section>
      <section className="bg-white rounded-2xl border border-[#DDD3C6] p-6 space-y-4">
        <h3 className="font-display text-2xl">3 · Ou : code à coller manuellement</h3>
        <p className="text-sm text-[#6B5F53]">Shopify › Boutique en ligne › Thèmes › Personnaliser › modèle « Produits » › Ajouter un bloc/section « Liquid personnalisé » › coller ce code › Enregistrer.</p>
        <pre className="text-xs bg-[#F6F0EB] rounded-xl p-4 overflow-x-auto max-h-72" data-testid="shopify-snippet">{snippet}</pre>
        <button onClick={copy} className="btn-outline text-sm" data-testid="shopify-copy-snippet"><Copy size={14} /> Copier le code</button>
      </section>
    </div>
  );
};
